import { Client, CompanyInfo, UserAccess } from './react-app-env'

export function showKAlert(variant: string, message: string | Array<string>) {
  window.kAlert = {
    variant: variant,
    message: message,
  }

  window.dispatchEvent(new Event('showKAlert'))
}
export function hideKAlert() {
  window.dispatchEvent(new Event('hideKAlert'))
}

export function getDataSecurityCompanyName() {
  return window.location.hostname.endsWith('.ch') ? 'Data Security AG' : 'Data Security GmbH'
}

export function setRedirectUrl(redirectUrl: string) {
  sessionStorage.setItem('redirectUrl', redirectUrl)
}
export function getRedirectUrl() {
  return sessionStorage.getItem('redirectUrl') ?? '/'
}

export function setCompanyInfo(companyInfo: CompanyInfo) {
  sessionStorage.setItem('companyInfo', JSON.stringify(companyInfo))
}
export function getCompanyInfo() {
  return JSON.parse(sessionStorage.getItem('companyInfo') as string) as CompanyInfo
}

// DS|Sign and DS|Identity send personal data to external providers, so they are
// unavailable once the tenant enabled PII encryption. The backend enforces this;
// this mirrors the cached company flag for the UI.
export function isPiiEncryptionEnabled(): boolean {
  return !!getCompanyInfo()?.piiEncryptionEnabled
}

// sessionStorage.companyInfo is populated asynchronously on app load (see Layout.tsx).
// If a caller needs it before that resolves (fresh tab, slow network), fetch it here
// instead of sending an empty companyInfo to the backend.
export async function ensureCompanyInfo(): Promise<string | null> {
  const existing = sessionStorage.getItem('companyInfo')
  if (existing) return existing

  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/company/info`)
    if (!res.ok) return null
    const info = await res.json()
    setCompanyInfo(info)
    return sessionStorage.getItem('companyInfo')
  } catch {
    return null
  }
}

export function setAccessToken(accessToken: string) {
  sessionStorage.setItem('accessToken', accessToken)
}
export function getAccessToken() {
  return sessionStorage.getItem('accessToken')
}

// Moment the user last completed a PIN / 2FA verification. The global fetch
// wrapper consults this to ignore the burst of 401/403s that stale requests -
// already in flight with the now-replaced pre-auth token when the code was
// submitted - produce right after login. Without the grace window a single such
// response tore the fresh session down and bounced the user back to login.
let lastVerifiedAuthAt = 0
export function markAuthenticated() {
  lastVerifiedAuthAt = Date.now()
}
export function wasRecentlyAuthenticated(withinMs = 5000) {
  return Date.now() - lastVerifiedAuthAt < withinMs
}

export function setSuperAdminToken(token: string) {
  sessionStorage.setItem('superAdminToken', token)
}

export function getSuperAdminToken() {
  return sessionStorage.getItem('superAdminToken')
}

export function removeSuperAdminAccess() {
  sessionStorage.removeItem('superAdminToken')
}

export function setUserAccess(userAccess: UserAccess) {
  sessionStorage.setItem('userAccess', JSON.stringify(userAccess))
}
export function getUserAccess() {
  return JSON.parse(sessionStorage.getItem('userAccess') as string) as UserAccess
}

export function isUserLoggedIn() {
  return !!getUserAccess()?.permissions
}

// Stammdaten and Survey (Erhebungsbogen) forms are editable only by admins and
// the main account owner. Other team members get a read-only view. A client
// filling in their own form through a PIN link is not a logged-in team member,
// so they keep full edit access.
export function canEditKycForms() {
  const permissions = getUserAccess()?.permissions
  if (!permissions) return true
  return !!permissions.admin || !!permissions.main
}

// Pages reachable without an established session (invite/reset links, login, etc).
// A stray 401/403 from a background request on one of these must not bounce the user away.
export const publicAuthPaths = [
  '/user/confirm',
  '/user/password-set',
  '/user/login',
  '/user/forgot-password',
  '/user/pincode-validation',
  '/user/twofa-verify',
  '/user/twofa/setup',
]

export function isPublicAuthPath(pathname: string) {
  return publicAuthPaths.some(p => pathname.startsWith(p))
}

export function removeAccess() {
  sessionStorage.removeItem('accessToken')
  sessionStorage.removeItem('userAccess')
  sessionStorage.removeItem('companyInfo')
  sessionStorage.removeItem('redirectUrl')
}

// Finalizes a pre-auth session through the PIN endpoint. The manual PIN page passes
// the typed code; the Microsoft SSO login passes an empty one (login issues the
// access record with pinRequired=false, so no code is expected).
export async function submitPinCode(pinCode: string, preAuthToken: string) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/user/pincode-validation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(preAuthToken ? { accessToken: preAuthToken } : {}),
    },
    body: JSON.stringify({ pinCode }),
  })
  return response.json()
}

export function disableClickables() {
  const focused = document.activeElement
  document.querySelectorAll('button, a, input, select, textarea').forEach((item: any) => {
    if (item === focused) return
    item.classList.add('non-clickable')
    item.disabled = true
  })
}

export function enableClickables() {
  document.querySelectorAll('button, a, input, select, textarea').forEach((item: any) => {
    item.classList.remove('non-clickable')
    item.disabled = false
  })
}

export function updateAccessName(userData: any) {
  const userAccess: UserAccess = getUserAccess()
  userAccess.name = `${userData.lastName} ${userData.firstName}`
  userAccess.email = userData.email
  setUserAccess(userAccess)
}

export function isCurrentUser(userData: any) {
  const accessToken = getAccessToken()
  const parsedToken = !!accessToken && JSON.parse(accessToken)
  return parsedToken?.userId === userData._id
}

export function filterColumns(data: Client[]): { label: string; key: string }[] {
  if (!data.length) return []
  const columns = Object.keys(data[0])
  const headers = columns.map(col => ({ label: camelCase(col), key: col }))
  return headers
}

export function camelCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export async function apiRequest(url: string, method: string, body?: any, apiKey?: string) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (apiKey) {
    headers['x-api-key'] = `${apiKey}`
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
  return response.json()
}

export function getCurrentDate(separator = '') {
  let newDate = new Date()
  let date = newDate.getDate()
  let month = newDate.getMonth() + 1
  let year = newDate.getFullYear()

  return `${date}.${month < 10 ? `0${month}` : `${month}`}.${year}`
}

export function cleanFilter(filter: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(filter).filter(
      ([_, value]) => value !== null && value !== undefined && value !== ''
    )
  )
}

export const addToLocalStorageArray = (newItem: any, arrayName: string) => {
  const existingData = JSON.parse(localStorage.getItem(arrayName) || '[]')
  const updatedData = [...existingData, newItem]
  localStorage.setItem(arrayName, JSON.stringify(updatedData))
}

export function removeBeforeUnderscore(str: string) {
  const index = str.indexOf('_')
  return index !== -1 ? str.slice(index + 2) : str
}

export function roundCredit(value: unknown): string | null {
  const num = Number(value)
  return Number.isFinite(num) ? String(Math.round(num * 10) / 10) : null
}

export const formatDDMMYYYY = (input: Date | string | number | null): string => {
  if (!input) return ''

  const d = input instanceof Date ? input : new Date(input)

  if (isNaN(d.getTime())) return ''

  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()

  return `${day}.${month}.${year}`
}

export const parseDDMMYYYY = (s?: string | null) => {
  if (!s) return null
  // Only complete dates count, so a half-typed value never resolves to e.g. year 2
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s.trim())
  if (!match) return null
  const [dd, mm, yyyy] = match.slice(1).map(Number)
  const date = new Date(yyyy, mm - 1, dd)
  return date.getDate() === dd && date.getMonth() === mm - 1 ? date : null
}

// Keeps manually typed dates within the DD.MM.YYYY shape the date pickers expect
export const sanitizeDDMMYYYYInput = (value: string): string =>
  value.replace(/[^\d.]/g, '').slice(0, 10)

export function setArrayElement<T>(array: T[], index: number, value: any): T[] {
  if (index < 0) return array

  const newArray = [...array]

  // Ensure the array is long enough
  if (index >= newArray.length) {
    newArray.length = index + 1
  }

  newArray[index] = value
  return newArray
}

export function updateNestedField<T extends object>(obj: T, path: string, value: any): T {
  const keys = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
  if (!keys.length) return obj

  const out: any = Array.isArray(obj) ? [...(obj as any)] : { ...(obj as any) }
  let cur: any = out

  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    const nextKey = keys[i + 1]
    const isNextIndex = /^\d+$/.test(nextKey)

    if (cur[k] !== undefined && cur[k] !== null) {
      cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] }
    } else {
      cur[k] = isNextIndex ? [] : {}
    }
    cur = cur[k]
  }

  const last = keys[keys.length - 1]
  if (/^\d+$/.test(last) && Array.isArray(cur)) {
    const idx = Number(last)
    cur = setArrayElement(cur, idx, value)

    // Update the parent reference
    if (keys.length > 1) {
      const parentPath = keys.slice(0, -1).join('.')
      const parent = getDeep(out, parentPath)
      if (parent && Array.isArray(parent)) {
        updateNestedField(out, parentPath, cur)
      }
    }
  } else {
    cur[last] = value
  }
  return out
}

export function getDeepView<T = any>(obj: any, path: string, fallback?: T): T | undefined {
  if (obj == null || !path) return fallback

  const keys = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)

  let cur: any = obj
  for (const k of keys) {
    console.log('k: ' + k)
    if (cur == null) return fallback
    cur = cur.stammdaten[k as keyof typeof cur]
  }
  return (cur === undefined ? fallback : cur) as T
}

export function getDeep<T = any>(obj: any, path: string, fallback?: T): T | undefined {
  if (obj == null || !path) return fallback
  const keys = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
  let cur: any = obj
  for (const k of keys) {
    if (cur == null) return fallback
    cur = cur[k as keyof typeof cur]
  }
  return (cur === undefined ? fallback : cur) as T
}

export function formatDate(d?: string) {
  if (!d) return '—'
  try {
    const date = new Date(d)
    return date.toLocaleString()
  } catch {
    return d
  }
}
